import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  CalendarDays,
  Edit3,
  Euro,
  GlassWater,
  MapPin,
  Minus,
  MoveRight,
  MoreHorizontal,
  PackagePlus,
  RotateCcw,
  Store,
  Undo2,
  Wine as WineIcon,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { AppShell } from "../components/AppShell";
import { LocationSelect } from "../components/LocationSelect";
import { Modal } from "../components/Modal";
import { ErrorState, PageLoader } from "../components/States";
import { TastingBadge } from "../components/TastingBadge";
import { useToast } from "../components/Toast";
import { api, OFFLINE_DATA_CHANGED_EVENT } from "../lib/api";
import { todayInParis as today } from "../lib/date";
import {
  inventoryReturnPath,
  withInventoryReturn,
} from "../lib/inventory-navigation";
import {
  isOfflineTemporaryMovement,
  offlinePersistence,
} from "../lib/offline";
import { Link, useLocation, useParams } from "../lib/router";
import {
  activeCoravins,
  bottleLabel,
  currentYear,
  formatDate,
  formatMoney,
  getTastingStatus,
  movementLabel,
  tastingPeriod,
  vintageLabel,
  wineLocationStocks,
} from "../lib/wine";
import type {
  CellarLocation,
  CellarPlaceRecord,
  CoravinBottle,
  Movement,
  Wine,
  WineLocationStock,
} from "../types";

type CoravinAction = "coravin" | "finish-coravin" | "delete-coravin";
type OpenModal = "drink" | "purchase" | "correction" | "transfer" | CoravinAction | null;

const NO_LOCATION_VALUE = "__sans_emplacement__";
const locationValue = (id: string | null): string => id ?? NO_LOCATION_VALUE;
const locationId = (value: string): string | null =>
  value === NO_LOCATION_VALUE ? null : value;

function stockLocationLabel(stock: WineLocationStock): string {
  return stock.emplacement || stock.emplacement_nom || stock.lieu || "Sans emplacement";
}

function actionLocations(
  wine: Wine,
  locations: readonly CellarLocation[],
): CellarLocation[] {
  const values = new Map(locations.map((location) => [location.id, location]));
  wineLocationStocks(wine).forEach((stock) => {
    if (!stock.emplacement_id || values.has(stock.emplacement_id)) return;
    const nom = stock.emplacement_nom || stock.emplacement || "Emplacement";
    values.set(stock.emplacement_id, {
      id: stock.emplacement_id,
      lieu_id: stock.lieu_id,
      lieu: stock.lieu,
      nom,
      libelle: stock.emplacement || (stock.lieu ? `${stock.lieu} - ${nom}` : nom),
      capacite_totale: null,
      nombre_fiches: 0,
      stock: stock.stock,
    });
  });
  return [...values.values()].sort((a, b) =>
    a.libelle.localeCompare(b.libelle, "fr", { sensitivity: "base" }),
  );
}

export function WineDetailPage() {
  const { id = "" } = useParams();
  const { search } = useLocation();
  const returnToInventory = inventoryReturnPath(search);
  const [wine, setWine] = useState<Wine | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState<OpenModal>(null);
  const [selectedCoravin, setSelectedCoravin] = useState<CoravinBottle | null>(null);
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  const [locations, setLocations] = useState<CellarLocation[]>([]);
  const [places, setPlaces] = useState<CellarPlaceRecord[]>([]);
  const { dismissToast, showToast } = useToast();
  const undoToasts = useRef(new Map<string, number>());

  useEffect(() => {
    const refreshOnline = () => setOnline(navigator.onLine !== false);
    window.addEventListener("online", refreshOnline);
    window.addEventListener("offline", refreshOnline);
    return () => {
      window.removeEventListener("online", refreshOnline);
      window.removeEventListener("offline", refreshOnline);
    };
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const loaded = await api.getWine(id);
      setWine(loaded);
      const locationData = await Promise.all([
        api.listLocations(),
        api.listPlaces(),
      ]).catch(() => null);
      if (locationData) {
        setLocations(locationData[0]);
        setPlaces(locationData[1]);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Cette fiche est inaccessible.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const refreshAfterSynchronization = () => void load();
    window.addEventListener(
      OFFLINE_DATA_CHANGED_EVENT,
      refreshAfterSynchronization,
    );
    return () =>
      window.removeEventListener(
        OFFLINE_DATA_CHANGED_EVENT,
        refreshAfterSynchronization,
      );
  }, [load]);

  const handleDrink = async (
    date: string,
    note: string,
    emplacementId: string | null,
  ): Promise<void> => {
    if (!wine) return;
    const movement = await api.createMovement(wine.id, {
      quantite: -1,
      motif: "Bouteille bue",
      date_effective: date,
      note,
      emplacement_id: emplacementId,
    });
    if (!movement) throw new Error("La consommation n’a pas été créée.");
    setModal(null);
    await load();
    const toastId = showToast({
      message: "Une bouteille a été retirée du stock.",
      actionLabel: "Annuler",
      duration: 9000,
      onAction: async () => {
        undoToasts.current.delete(movement.id);
        try {
          if (isOfflineTemporaryMovement(movement)) {
            await offlinePersistence.cancelQueuedMovement(
              movement.client_operation_id,
            );
          } else {
            if (movement.date_modification) {
              await api.deleteMovement(
                wine.id,
                movement.id,
                movement.date_modification,
              );
            } else {
              await api.deleteMovement(wine.id, movement.id);
            }
          }
          await load();
          showToast({ message: "La bouteille est de nouveau en stock." });
        } catch (caught) {
          showToast({
            message: caught instanceof Error ? caught.message : "L’annulation a échoué.",
            tone: "info",
          });
        }
      },
    });
    undoToasts.current.set(movement.id, toastId);
  };

  const dismissImmediateUndo = useCallback(
    (movementId: string) => {
      const toastId = undoToasts.current.get(movementId);
      if (toastId == null) return;
      dismissToast(toastId);
      undoToasts.current.delete(movementId);
    },
    [dismissToast],
  );

  const showTraceableUndo = (
    movement: Movement,
    message: string,
    successMessage: string,
  ) => {
    if (!wine) return;
    const toastId = showToast({
      message,
      actionLabel: "Annuler",
      duration: 9000,
      onAction: async () => {
        undoToasts.current.delete(movement.id);
        try {
          if (isOfflineTemporaryMovement(movement)) {
            await offlinePersistence.cancelQueuedMovement(
              movement.client_operation_id,
            );
          } else {
            const cancellation = {
              date_effective: today(),
              note: null,
            };
            if (movement.date_modification) {
              await api.cancelMovement(
                wine.id,
                movement.id,
                cancellation,
                movement.date_modification,
              );
            } else {
              await api.cancelMovement(wine.id, movement.id, cancellation);
            }
          }
          await load();
          showToast({ message: successMessage });
        } catch (caught) {
          showToast({
            message:
              caught instanceof Error
                ? caught.message
                : "L’annulation a échoué.",
            tone: "info",
          });
        }
      },
    });
    undoToasts.current.set(movement.id, toastId);
  };

  if (loading) {
    return <AppShell><div className="page"><PageLoader label="Ouverture de la fiche…" /></div></AppShell>;
  }

  if (error || !wine) {
    return (
      <AppShell>
        <div className="page narrow-page">
          <Link className="back-link" to={returnToInventory}><ArrowLeft size={17} /> Retour à l’inventaire</Link>
          <ErrorState title="Fiche introuvable" message={error || "Ce vin n’existe plus."} onRetry={() => void load()} />
        </div>
      </AppShell>
    );
  }

  const status = getTastingStatus(wine);
  const movements = [...(wine.mouvements ?? [])].sort((a, b) => {
    const effective = new Date(b.date_effective || b.date_creation).getTime()
      - new Date(a.date_effective || a.date_creation).getTime();
    return effective || new Date(b.date_creation).getTime() - new Date(a.date_creation).getTime();
  });
  const stocks = wineLocationStocks(wine).filter((item) => item.stock > 0);
  const availableLocations = actionLocations(wine, locations);
  const coravins = activeCoravins(wine);
  const finishedCoravins = (wine.coravins ?? []).filter((bottle) => bottle.date_fin);
  const uncoravinedCount = wine.stock - coravins.length;

  return (
    <AppShell>
      <div className="page detail-page">
        <Link className="back-link" to={returnToInventory}><ArrowLeft size={17} /> Retour à l’inventaire</Link>

        <article>
          <header className="wine-detail-header">
            <div className="detail-identity">
              <div className="detail-kicker">
                <span>{wine.categorie}</span>
                <span aria-hidden="true">•</span>
                <span>{vintageLabel(wine)}</span>
              </div>
              <h1>{wine.nom}</h1>
              <p className="detail-producer">{wine.producteur}</p>
              {[wine.region, wine.appellation].filter(Boolean).length ? (
                <p className="detail-origin">{[wine.region, wine.appellation].filter(Boolean).join(" · ")}</p>
              ) : null}
            </div>
            <Link className="button button-secondary edit-button" to={withInventoryReturn(`/vins/${wine.id}/modifier`, returnToInventory)}>
              <Edit3 size={17} aria-hidden="true" /> Modifier la fiche
            </Link>
          </header>

          <section className="detail-essentials" aria-label="Stock et emplacement">
            <div className={`detail-stock ${wine.stock === 0 ? "detail-stock-empty" : ""}`}>
              <span className="detail-icon"><WineIcon size={23} aria-hidden="true" /></span>
              <div><strong>{wine.stock}</strong><span>{wine.stock === 1 ? "bouteille disponible" : "bouteilles disponibles"}</span>{coravins.length ? <span className="detail-coravin-count">dont {coravins.length} coravinée{coravins.length > 1 ? "s" : ""}</span> : null}</div>
              {wine.stock === 0 ? <span className="exhausted-label">Épuisé</span> : null}
            </div>
            <div className="detail-location detail-location-breakdown">
              <span className="detail-icon"><MapPin size={23} aria-hidden="true" /></span>
              <div>
                <span>{stocks.length > 1 ? "Répartition" : "Emplacement"}</span>
                {stocks.length ? (
                  <ul>
                    {stocks.map((item) => (
                      <li key={locationValue(item.emplacement_id)}>
                        <strong>{stockLocationLabel(item)}</strong>
                        <b>{item.stock}</b>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <strong>{wine.emplacement || "Aucune bouteille en stock"}</strong>
                )}
              </div>
            </div>
          </section>

          <section className="detail-actions" aria-label="Actions sur le stock">
            <button
              className="button action-drink"
              type="button"
              onClick={() => setModal("drink")}
              disabled={uncoravinedCount < 1}
            >
              <GlassWater size={20} aria-hidden="true" />
              <span><strong>Boire une bouteille</strong><small>{uncoravinedCount > 0 ? "Retirer 1 du stock" : wine.stock ? "Utiliser « Terminer » ci-dessous" : "Stock épuisé"}</small></span>
            </button>
            <button className="button action-card" type="button" onClick={() => setModal("purchase")}>
              <PackagePlus size={20} aria-hidden="true" />
              <span><strong>Ajouter des bouteilles</strong><small>Enregistrer un achat</small></span>
            </button>
            <button className="button action-card" type="button" onClick={() => setModal("correction")}>
              <RotateCcw size={20} aria-hidden="true" />
              <span><strong>Corriger le stock</strong><small>Après comptage réel</small></span>
            </button>
            <button
              className="button action-card"
              type="button"
              onClick={() => setModal("transfer")}
              disabled={wine.stock < 1}
            >
              <MoveRight size={20} aria-hidden="true" />
              <span><strong>Déplacer des bouteilles</strong><small>Changer leur emplacement</small></span>
            </button>
          </section>

          <section className="detail-section coravin-section" aria-labelledby="coravin-heading">
            <div className="section-heading">
              <div><p className="eyebrow">Suivi de dégustation</p><h2 id="coravin-heading">Bouteilles coravinées</h2></div>
              <button className="button button-secondary" type="button" disabled={!online || uncoravinedCount < 1} onClick={() => { setSelectedCoravin(null); setModal("coravin"); }}>
                <WineIcon size={17} aria-hidden="true" /> Coraviner une bouteille
              </button>
            </div>
            <p className="section-help">Une bouteille coravinée reste dans le stock jusqu’à ce qu’elle soit terminée.</p>
            {!online ? <p className="field-help" role="status">Une connexion est nécessaire pour coraviner une bouteille, la terminer ou annuler son marquage.</p> : null}
            {coravins.length ? (
              <ul className="coravin-list">
                {coravins.map((bottle, index) => (
                  <li key={bottle.id}>
                    <div className="coravin-bottle-info">
                      <strong>Bouteille {index + 1} · coravinée le {formatDate(bottle.date_coravin)}</strong>
                      <p className="timeline-location"><MapPin size={14} aria-hidden="true" />{bottle.emplacement || bottle.lieu || "Sans emplacement"}</p>
                      {bottle.note ? <p className="timeline-note">{bottle.note}</p> : null}
                    </div>
                    <div className="coravin-bottle-actions">
                      <button className="button button-secondary" type="button" disabled={!online} onClick={() => { setSelectedCoravin(bottle); setModal("finish-coravin"); }}>Terminer cette bouteille</button>
                      <button className="button button-ghost" type="button" disabled={!online} onClick={() => { setSelectedCoravin(bottle); setModal("delete-coravin"); }}>Annuler le marquage</button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : <p className="history-empty">Aucune bouteille coravinée en cours.</p>}
            {finishedCoravins.length ? (
              <details className="coravin-history">
                <summary>Historique Coravin · {finishedCoravins.length} bouteille{finishedCoravins.length > 1 ? "s" : ""} terminée{finishedCoravins.length > 1 ? "s" : ""}</summary>
                <ul className="coravin-list">
                  {[...finishedCoravins].sort((a, b) => (b.date_fin ?? "").localeCompare(a.date_fin ?? "")).map((bottle) => (
                    <li key={bottle.id}><div className="coravin-bottle-info">
                      <strong>Coravinée le {formatDate(bottle.date_coravin)} · terminée le {formatDate(bottle.date_fin)}</strong>
                      <p className="timeline-location"><MapPin size={14} aria-hidden="true" />{bottle.emplacement || bottle.lieu || "Sans emplacement"}</p>
                      {bottle.note ? <p className="timeline-note">{bottle.note}</p> : null}
                    </div></li>
                  ))}
                </ul>
              </details>
            ) : null}
          </section>

          <div className="detail-columns">
            <section className="detail-section tasting-section">
              <div className="section-heading">
                <div><p className="eyebrow">Dégustation</p><h2>Le bon moment</h2></div>
                <TastingBadge
                  status={status}
                  deadlinePassed={
                    wine.fin_deg != null && wine.fin_deg < currentYear()
                  }
                />
              </div>
              <div className="period-row">
                <CalendarDays size={20} aria-hidden="true" />
                <div><span>Période conseillée</span><strong>{tastingPeriod(wine)}</strong></div>
              </div>
              {status === "À garder" ? <p className="section-help">Ce vin gagnera probablement à patienter encore un peu.</p> : null}
              {status === "À boire dès maintenant" ? <p className="section-help">Ce vin peut être dégusté dès maintenant.</p> : null}
            </section>

            <section className="detail-section wine-info-section">
              <p className="eyebrow">Informations</p>
              <h2>À propos du vin</h2>
              <dl className="wine-facts">
                <div><dt>Format</dt><dd>{wine.format || "75 cl"}</dd></div>
                <div><dt>Millésime</dt><dd>{vintageLabel(wine)}</dd></div>
                {wine.appellation ? <div><dt>Appellation</dt><dd>{wine.appellation}</dd></div> : null}
                {wine.region ? <div><dt>Région / pays</dt><dd>{wine.region}</dd></div> : null}
                {wine.cepages ? <div className="wine-fact-wide"><dt>Cépage(s)</dt><dd>{wine.cepages}</dd></div> : null}
                {wine.vinification_elevage ? <div className="wine-fact-wide"><dt>Vinification et élevage</dt><dd>{wine.vinification_elevage}</dd></div> : null}
              </dl>
              {wine.note ? <blockquote className="wine-note"><span>Note personnelle</span><p>{wine.note}</p></blockquote> : null}
            </section>
          </div>

          <History
            wineId={wine.id}
            stock={wine.stock}
            locationStocks={wineLocationStocks(wine)}
            movements={movements}
            historyAvailable={wine.mouvements !== undefined}
            onActionStart={dismissImmediateUndo}
            onChanged={load}
          />
        </article>
      </div>

      <DrinkModal open={modal === "drink"} wine={wine} onClose={() => setModal(null)} onConfirm={handleDrink} />
      <CoravinModal
        action={modal === "coravin" || modal === "finish-coravin" || modal === "delete-coravin" ? modal : null}
        bottle={selectedCoravin}
        wine={wine}
        online={online}
        onClose={() => setModal(null)}
        onSaved={async (action) => {
          setModal(null);
          setSelectedCoravin(null);
          await load();
          showToast({ message: action === "coravin" ? "La bouteille est marquée comme coravinée. Le stock est inchangé." : action === "finish-coravin" ? "La bouteille coravinée est terminée et a été retirée du stock." : "Le marquage Coravin a été annulé. Le stock est inchangé." });
        }}
      />
      <PurchaseModal
        open={modal === "purchase"}
        wine={wine}
        locations={availableLocations}
        places={places}
        onClose={() => setModal(null)}
        onSaved={async (movement) => {
          setModal(null);
          await load();
          showTraceableUndo(
            movement,
            "L’achat a été ajouté à l’historique.",
            "L’achat a été annulé et reste visible dans l’historique.",
          );
        }}
      />
      <CorrectionModal
        open={modal === "correction"}
        wine={wine}
        locations={availableLocations}
        places={places}
        onClose={() => setModal(null)}
        onSaved={async (movement) => {
          setModal(null);
          if (!movement) {
            showToast({ message: "Le stock est déjà correct.", tone: "info" });
            return;
          }
          await load();
          showTraceableUndo(
            movement,
            "Le stock a été corrigé.",
            "La correction a été annulée et reste visible dans l’historique.",
          );
        }}
      />
      <TransferModal
        open={modal === "transfer"}
        wine={wine}
        locations={availableLocations}
        places={places}
        onClose={() => setModal(null)}
        onSaved={async (quantity) => {
          setModal(null);
          await load();
          showToast({
            message: `${quantity} bouteille${quantity === 1 ? " a" : "s ont"} été déplacée${quantity === 1 ? "" : "s"}.`,
          });
        }}
      />
    </AppShell>
  );
}

function movementDisplayLabel(
  movement: Movement,
  movementsById: Map<string, Movement>,
): string {
  if (!movement.mouvement_annule_id) return movementLabel(movement);
  const original = movementsById.get(movement.mouvement_annule_id);
  return original
    ? `Annulation de « ${movementLabel(original)} »`
    : "Annulation d’un mouvement";
}

function History({
  wineId,
  stock,
  locationStocks,
  movements,
  historyAvailable,
  onActionStart,
  onChanged,
}: {
  wineId: string;
  stock: number;
  locationStocks: WineLocationStock[];
  movements: Movement[];
  historyAvailable: boolean;
  onActionStart: (movementId: string) => void;
  onChanged: () => Promise<void>;
}) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editingMovement, setEditingMovement] = useState<Movement | null>(null);
  const [cancellingMovement, setCancellingMovement] = useState<Movement | null>(null);
  const movementsById = new Map(movements.map((movement) => [movement.id, movement]));
  const cancelledMovementIds = new Set(
    movements.flatMap((movement) =>
      movement.mouvement_annule_id ? [movement.mouvement_annule_id] : [],
    ),
  );
  const transferIds = new Set(
    movements.flatMap((movement) =>
      movement.transfert_id ? [movement.transfert_id] : [],
    ),
  );
  const historyEntryCount =
    movements.filter((movement) => !movement.transfert_id).length +
    transferIds.size;
  const displayedTransferIds = new Set<string>();

  useEffect(() => {
    if (!openMenuId) return;
    const closeOutside = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Element && !target.closest(".movement-menu")) {
        setOpenMenuId(null);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenMenuId(null);
    };
    document.addEventListener("click", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("click", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [openMenuId]);

  return (
    <>
      <section className="detail-section history-section">
        <div className="section-heading">
          <div><p className="eyebrow">Traçabilité</p><h2>Historique</h2></div>
          <span className="movement-count">{historyEntryCount} opération{historyEntryCount === 1 ? "" : "s"}</span>
        </div>
        {movements.length ? (
          <ol className="timeline">
            {movements.map((movement) => {
              const transferId = movement.transfert_id;
              if (transferId && displayedTransferIds.has(transferId)) return null;
              if (transferId) displayedTransferIds.add(transferId);
              const transferMovements = transferId
                ? movements.filter((item) => item.transfert_id === transferId)
                : [];
              const transferSource = transferMovements.find((item) => item.quantite < 0);
              const transferDestination = transferMovements.find((item) => item.quantite > 0);
              const isTransfer = Boolean(
                transferId && transferSource && transferDestination,
              );
              const positive = movement.quantite > 0;
              const pending = isOfflineTemporaryMovement(movement);
              const cancelled = isTransfer
                ? transferMovements.some((item) => cancelledMovementIds.has(item.id))
                : cancelledMovementIds.has(movement.id);
              const compensation = isTransfer
                ? transferMovements.some((item) => Boolean(item.mouvement_annule_id))
                : Boolean(movement.mouvement_annule_id);
              const label = isTransfer
                ? "Transfert de bouteilles"
                : movementDisplayLabel(movement, movementsById);
              const canCancel =
                !pending &&
                movement.motif !== "Stock initial" &&
                !compensation &&
                !cancelled;
              const dateLabel = formatDate(
                movement.date_effective || movement.date_creation,
              );
              return (
                <li
                  key={movement.id}
                  className={`${cancelled ? "timeline-cancelled" : ""} ${compensation ? "timeline-compensation" : ""} ${isTransfer ? "timeline-transfer" : ""}`.trim()}
                >
                  <span className={`timeline-icon ${isTransfer ? "transfer" : positive ? "positive" : "negative"}`}>
                    {isTransfer ? <ArrowRight size={16} aria-hidden="true" /> : positive ? <ArrowUp size={16} aria-hidden="true" /> : <ArrowDown size={16} aria-hidden="true" />}
                  </span>
                  <div className="timeline-content">
                    <div className="timeline-title">
                      <div className="timeline-title-main">
                        <strong>{label}</strong>
                        {pending ? <span className="movement-status movement-pending">En attente</span> : null}
                        {cancelled ? <span className="movement-status">Annulé</span> : null}
                      </div>
                      <div className="timeline-title-actions">
                        <span className={`movement-quantity ${isTransfer ? "" : positive ? "movement-positive" : "movement-negative"}`}>{isTransfer ? Math.abs(transferSource?.quantite ?? movement.quantite) : <>{positive ? "+" : ""}{movement.quantite}</>}</span>
                        {!pending ? <div className="movement-menu">
                          <button
                            className="movement-menu-trigger"
                            type="button"
                            aria-label={`Actions pour ${label}, ${dateLabel}`}
                            aria-expanded={openMenuId === movement.id}
                            aria-controls={`movement-actions-${movement.id}`}
                            onClick={() =>
                              setOpenMenuId((current) =>
                                current === movement.id ? null : movement.id,
                              )
                            }
                          >
                            <MoreHorizontal size={19} aria-hidden="true" />
                          </button>
                          {openMenuId === movement.id ? (
                            <div
                              className="movement-menu-popover"
                              id={`movement-actions-${movement.id}`}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenMenuId(null);
                                  onActionStart(movement.id);
                                  setEditingMovement(movement);
                                }}
                              >
                                <Edit3 size={16} aria-hidden="true" /> Modifier les détails
                              </button>
                              {canCancel ? (
                                <button
                                  className="movement-menu-danger"
                                  type="button"
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    onActionStart(movement.id);
                                    setCancellingMovement(movement);
                                  }}
                                >
                                  <Undo2 size={16} aria-hidden="true" /> {isTransfer ? "Annuler ce transfert" : "Annuler ce mouvement"}
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </div> : null}
                      </div>
                    </div>
                    <p className="timeline-date">
                      {dateLabel}
                      {movement.date_modification &&
                      movement.date_modification !== movement.date_creation ? (
                        <span> · modifié le {formatDate(movement.date_modification)}</span>
                      ) : null}
                    </p>
                    {isTransfer ? (
                      <p className="timeline-location timeline-transfer-route">
                        <MapPin size={14} aria-hidden="true" />
                        <span>{transferSource?.emplacement || transferSource?.lieu || "Sans emplacement"}</span>
                        <ArrowRight size={14} aria-hidden="true" />
                        <span>{transferDestination?.emplacement || transferDestination?.lieu || "Sans emplacement"}</span>
                      </p>
                    ) : movement.emplacement || movement.lieu || movement.emplacement_id === null ? (
                      <p className="timeline-location">
                        <MapPin size={14} aria-hidden="true" />
                        {movement.emplacement || movement.lieu || "Sans emplacement"}
                      </p>
                    ) : null}
                    {movement.note ? <p className="timeline-note">{movement.note}</p> : null}
                    {movement.prix_unitaire != null || movement.vendeur ? (
                      <div className="movement-meta">
                        {movement.prix_unitaire != null ? <span><Euro size={14} /> {formatMoney(movement.prix_unitaire)} / bouteille</span> : null}
                        {movement.vendeur ? <span><Store size={14} /> {movement.vendeur}</span> : null}
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="history-empty">
            {historyAvailable
              ? "Aucun mouvement n’est encore enregistré."
              : "L’historique complet sera disponible après avoir ouvert cette fiche avec une connexion."}
          </p>
        )}
      </section>

      <EditMovementModal
        movement={editingMovement}
        wineId={wineId}
        onClose={() => setEditingMovement(null)}
        onSaved={onChanged}
      />
      <CancelMovementModal
        movement={cancellingMovement}
        wineId={wineId}
        stock={stock}
        locationStocks={locationStocks}
        onClose={() => setCancellingMovement(null)}
        onSaved={onChanged}
      />
    </>
  );
}

function EditMovementModal({
  movement,
  wineId,
  onClose,
  onSaved,
}: {
  movement: Movement | null;
  wineId: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [price, setPrice] = useState("");
  const [seller, setSeller] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { showToast } = useToast();

  useEffect(() => {
    if (!movement) return;
    setDate(movement.date_effective ?? "");
    setNote(movement.note ?? "");
    setPrice(
      movement.prix_unitaire == null
        ? ""
        : String(movement.prix_unitaire).replace(".", ","),
    );
    setSeller(movement.vendeur ?? "");
    setError("");
  }, [movement]);

  const isEntry =
    movement?.motif === "Achat" || movement?.motif === "Stock initial";
  const isConsumption = movement?.motif === "Bouteille bue";
  const isTransfer = movement?.motif === "Transfert" || Boolean(movement?.transfert_id);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!movement) return;
    const parsedPrice = price.trim() ? Number(price.replace(",", ".")) : null;
    if (
      isEntry &&
      parsedPrice != null &&
      (!Number.isFinite(parsedPrice) || parsedPrice < 0)
    ) {
      setError("Le prix doit être un montant positif ou nul.");
      return;
    }
    if (isConsumption) {
      if (!date) {
        setError("Indiquez la date de consommation.");
        return;
      }
      if (date > today()) {
        setError("La date de consommation ne peut pas être dans le futur.");
        return;
      }
    }
    setSaving(true);
    setError("");
    try {
      const update = {
        date_effective: date || null,
        note: note.trim() || null,
        ...(isEntry
          ? {
              prix_unitaire: parsedPrice,
              vendeur: seller.trim() || null,
            }
          : {}),
      };
      if (movement.date_modification) {
        await api.updateMovement(
          wineId,
          movement.id,
          update,
          movement.date_modification,
        );
      } else {
        await api.updateMovement(wineId, movement.id, update);
      }
      onClose();
      await onSaved();
      showToast({ message: "Les détails du mouvement ont été mis à jour." });
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "La modification du mouvement a échoué.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={Boolean(movement)}
      onClose={onClose}
      title="Modifier les détails"
      description={isTransfer ? "La date et la note seront appliquées aux deux lignes liées du transfert." : "La quantité et le motif resteront inchangés."}
    >
      <form onSubmit={submit}>
        {movement ? (
          <div className="movement-readonly-summary">
            <span>{movementLabel(movement)}</span>
            <strong>{isTransfer ? Math.abs(movement.quantite) : <>{movement.quantite > 0 ? "+" : ""}{movement.quantite}</>}</strong>
          </div>
        ) : null}
        <div className={`form-grid ${isEntry ? "two-columns" : ""}`}>
          <label className="field">
            <span>{isConsumption ? "Date de consommation" : <>Date du mouvement <em>facultative</em></>}</span>
            <input type="date" required={isConsumption} max={isConsumption ? today() : undefined} value={date} onChange={(event) => setDate(event.target.value)} />
          </label>
          {isEntry ? (
            <label className="field">
              <span>Prix par bouteille <em>facultatif</em></span>
              <div className="input-with-suffix">
                <input
                  type="text"
                  inputMode="decimal"
                  value={price}
                  onChange={(event) => setPrice(event.target.value)}
                  placeholder="0,00"
                />
                <span>€</span>
              </div>
            </label>
          ) : null}
          {isEntry ? (
            <label className="field">
              <span>Vendeur / provenance <em>facultatif</em></span>
              <input
                value={seller}
                onChange={(event) => setSeller(event.target.value)}
                placeholder="Caviste, domaine…"
              />
            </label>
          ) : null}
        </div>
        <label className="field">
          <span>Note <em>facultative</em></span>
          <textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
        </label>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="modal-actions">
          <button className="button button-ghost" type="button" onClick={onClose}>Annuler</button>
          <button className="button button-primary" type="submit" disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer les détails"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function CancelMovementModal({
  movement,
  wineId,
  stock,
  locationStocks,
  onClose,
  onSaved,
}: {
  movement: Movement | null;
  wineId: string;
  stock: number;
  locationStocks: WineLocationStock[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { showToast } = useToast();

  useEffect(() => {
    if (!movement) return;
    setDate(today());
    setNote("");
    setError("");
  }, [movement]);

  const nextStock = movement ? stock - movement.quantite : stock;
  const isTransfer = movement?.motif === "Transfert" || Boolean(movement?.transfert_id);
  const displayedNextStock = isTransfer ? stock : nextStock;
  const currentLocationStock = movement
    ? locationStocks.find(
        (item) => item.emplacement_id === (movement.emplacement_id ?? null),
      )?.stock ?? stock
    : stock;
  const nextLocationStock = movement
    ? currentLocationStock - movement.quantite
    : currentLocationStock;
  const blocked = displayedNextStock < 0 || (!isTransfer && nextLocationStock < 0);
  const compareLocation = !isTransfer && locationStocks.length > 1;
  const comparisonBefore = compareLocation ? currentLocationStock : stock;
  const comparisonAfter = compareLocation ? nextLocationStock : displayedNextStock;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!movement || blocked) return;
    setSaving(true);
    setError("");
    try {
      const cancellation = {
        date_effective: date || null,
        note: note.trim() || null,
      };
      if (movement.date_modification) {
        await api.cancelMovement(
          wineId,
          movement.id,
          cancellation,
          movement.date_modification,
        );
      } else {
        await api.cancelMovement(wineId, movement.id, cancellation);
      }
      onClose();
      await onSaved();
      showToast({ message: "Le mouvement a été annulé et reste traçable." });
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "L’annulation du mouvement a échoué.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={Boolean(movement)}
      onClose={onClose}
      title={isTransfer ? "Annuler ce transfert ?" : "Annuler ce mouvement ?"}
      description={isTransfer ? "Un contre-transfert lié sera ajouté à l’historique." : "Une écriture compensatrice sera ajoutée à l’historique."}
      size="small"
    >
      <form onSubmit={submit}>
        {movement ? (
          <div className="modal-callout cancellation-explanation">
            <Undo2 size={18} aria-hidden="true" />
            <p>
              <strong>{isTransfer ? `Transfert (${Math.abs(movement.quantite)})` : `${movementLabel(movement)} (${movement.quantite > 0 ? "+" : ""}${movement.quantite})`}</strong> restera visible avec le statut « Annulé ».
            </p>
          </div>
        ) : null}
        <div
          className={`stock-comparison cancellation-stock ${blocked ? "is-blocked" : ""}`}
          aria-label={`Stock actuel ${comparisonBefore}, après annulation ${comparisonAfter}`}
        >
          <div><span>{compareLocation ? "Stock ici" : "Stock actuel"}</span><strong>{comparisonBefore}</strong></div>
          <span aria-hidden="true">→</span>
          <div><span>Après annulation</span><strong>{comparisonAfter}</strong></div>
        </div>
        {blocked ? (
          <p className="form-error cancellation-blocked" role="alert">
            <AlertTriangle size={18} aria-hidden="true" />
            Cette annulation ferait passer le stock sous zéro à cet emplacement. Corrigez d’abord le stock réel.
          </p>
        ) : null}
        <label className="field">
          <span>Date de l’annulation <em>facultative</em></span>
          <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </label>
        <label className="field">
          <span>Note <em>facultative</em></span>
          <textarea
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Erreur de saisie, achat enregistré deux fois…"
          />
        </label>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="modal-actions">
          <button className="button button-ghost" type="button" onClick={onClose}>Conserver le mouvement</button>
          <button className="button button-danger" type="submit" disabled={saving || blocked}>
            <Undo2 size={17} aria-hidden="true" /> {saving ? "Annulation…" : isTransfer ? "Annuler le transfert" : "Confirmer l’annulation"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function CoravinModal({
  action,
  bottle,
  wine,
  online,
  onClose,
  onSaved,
}: {
  action: CoravinAction | null;
  bottle: CoravinBottle | null;
  wine: Wine;
  online: boolean;
  onClose: () => void;
  onSaved: (action: CoravinAction) => Promise<void>;
}) {
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const stocks = wineLocationStocks(wine).filter((item) =>
    item.stock > activeCoravins(wine, item.emplacement_id).length,
  );
  const selectedStock = stocks.find((item) => locationValue(item.emplacement_id) === selectedLocation);
  const creating = action === "coravin";
  const removing = action === "delete-coravin";
  const title = creating ? "Coraviner une bouteille" : removing ? "Annuler le marquage Coravin" : "Terminer cette bouteille";

  useEffect(() => {
    if (!action) return;
    setDate(today());
    setNote("");
    setError("");
    setSelectedLocation(stocks.length === 1 ? locationValue(stocks[0].emplacement_id) : "");
  }, [action, bottle?.id, wine.id]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!action || inFlight.current) return;
    if (!online) { setError("Une connexion est nécessaire pour enregistrer cette opération Coravin."); return; }
    if (creating && !selectedStock) { setError("Choisissez un emplacement avec une bouteille non coravinée."); return; }
    if (!removing && (!date || date > today())) { setError("Indiquez une date qui n’est pas dans le futur."); return; }
    if (!creating && !bottle) return;
    if (!creating && !removing && bottle && date < bottle.date_coravin) { setError("La date de fin ne peut pas précéder le coravinage."); return; }
    inFlight.current = true;
    setSaving(true);
    setError("");
    try {
      if (creating && selectedStock) {
        await api.createCoravin(wine.id, { emplacement_id: selectedStock.emplacement_id, date_effective: date, note: note.trim() || null });
      } else if (bottle && removing) {
        await api.deleteCoravin(wine.id, bottle.id, bottle.date_modification);
      } else if (bottle) {
        await api.finishCoravin(wine.id, bottle.id, { date_effective: date, note: note.trim() || null });
      }
      await onSaved(action);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "L’opération Coravin a échoué.");
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal open={action !== null} onClose={onClose} title={title} description={wine.nom} size="small">
      <form onSubmit={submit}>
        {creating ? (
          <label className="field">
            <span>Emplacement de la bouteille <b>*</b></span>
            <select required value={selectedLocation} onChange={(event) => setSelectedLocation(event.target.value)}>
              <option value="" disabled>Choisir un emplacement…</option>
              {stocks.map((item) => {
                const available = item.stock - activeCoravins(wine, item.emplacement_id).length;
                return <option key={locationValue(item.emplacement_id)} value={locationValue(item.emplacement_id)}>{stockLocationLabel(item)} — {available} non coravinée{available > 1 ? "s" : ""}</option>;
              })}
            </select>
          </label>
        ) : bottle ? (
          <p className="selected-location-summary"><MapPin size={16} aria-hidden="true" /><span>{bottle.emplacement || bottle.lieu || "Sans emplacement"} · coravinée le {formatDate(bottle.date_coravin)}</span></p>
        ) : null}
        <div className="modal-callout"><WineIcon size={18} aria-hidden="true" /><p>{creating ? "La bouteille reste comptée dans le stock. Elle apparaîtra dans les vins coravinés." : removing ? "Utilisez cette action pour corriger un marquage fait par erreur. Le suivi Coravin de cette bouteille sera supprimé et le stock restera inchangé." : <>Cette bouteille sera retirée du stock : <strong>{wine.stock - 1}</strong> restante{wine.stock - 1 > 1 ? "s" : ""}. Son suivi Coravin sera conservé dans l’historique.</>}</p></div>
        {!removing ? <>
          <label className="field"><span>{creating ? "Date de coravinage" : "Date de fin"}</span><input type="date" required min={creating ? undefined : bottle?.date_coravin} max={today()} value={date} onChange={(event) => setDate(event.target.value)} /></label>
          <label className="field"><span>Note de dégustation <em>facultative</em></span><textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Vos impressions sur cette bouteille…" /></label>
        </> : null}
        {!online ? <p className="field-help">Une connexion est nécessaire pour enregistrer cette opération Coravin.</p> : null}
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="modal-actions"><button className="button button-ghost" type="button" disabled={saving} onClick={onClose}>Annuler</button><button className="button button-primary" type="submit" disabled={saving || !online || (creating && !selectedStock)}>{saving ? "Enregistrement…" : creating ? "Marquer comme coravinée" : removing ? "Annuler le marquage" : "Terminer et retirer du stock"}</button></div>
      </form>
    </Modal>
  );
}

function DrinkModal({
  open,
  wine,
  onClose,
  onConfirm,
}: {
  open: boolean;
  wine: Wine;
  onClose: () => void;
  onConfirm: (date: string, note: string, emplacementId: string | null) => Promise<void>;
}) {
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const submissionInFlight = useRef(false);
  const stocks = wineLocationStocks(wine).filter((item) => item.stock > activeCoravins(wine, item.emplacement_id).length);
  const selectedStock = stocks.find(
    (item) => locationValue(item.emplacement_id) === selectedLocation,
  );

  useEffect(() => {
    if (!open) return;
    submissionInFlight.current = false;
    setDate(today());
    setNote("");
    setSelectedLocation(
      stocks.length === 1 ? locationValue(stocks[0].emplacement_id) : "",
    );
    setError("");
  }, [open, wine.id, wine.stock]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submissionInFlight.current) return;
    if (!selectedStock) {
      setError("Choisissez l’emplacement de la bouteille.");
      return;
    }
    if (!date) {
      setError("Indiquez la date de consommation.");
      return;
    }
    if (date > today()) {
      setError("La date de consommation ne peut pas être dans le futur.");
      return;
    }
    submissionInFlight.current = true;
    setSaving(true);
    setError("");
    try {
      await onConfirm(date, note.trim(), selectedStock.emplacement_id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Impossible de retirer la bouteille.");
    } finally {
      submissionInFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Boire une bouteille" description={`${wine.nom} · ${bottleLabel(wine.stock)} actuellement`} size="small">
      <form onSubmit={submit}>
        {activeCoravins(wine).length ? <p className="field-help">Pour une bouteille coravinée, utilisez « Terminer cette bouteille » dans la fiche.</p> : null}
        {stocks.length > 1 ? (
          <label className="field">
            <span>Emplacement de la bouteille <b>*</b></span>
            <select
              required
              value={selectedLocation}
              onChange={(event) => setSelectedLocation(event.target.value)}
            >
              <option value="" disabled>Choisir un emplacement…</option>
              {stocks.map((item) => (
                <option
                  key={locationValue(item.emplacement_id)}
                  value={locationValue(item.emplacement_id)}
                >
                  {stockLocationLabel(item)} — {bottleLabel(item.stock - activeCoravins(wine, item.emplacement_id).length)}{activeCoravins(wine).length ? ` non coravinée${item.stock - activeCoravins(wine, item.emplacement_id).length > 1 ? "s" : ""}` : ""}
                </option>
              ))}
            </select>
          </label>
        ) : selectedStock ? (
          <p className="selected-location-summary">
            <MapPin size={16} aria-hidden="true" />
            Retrait à <strong>{stockLocationLabel(selectedStock)}</strong>
          </p>
        ) : null}
        <div className="modal-callout"><Minus size={18} aria-hidden="true" /><p>{selectedStock ? <>À cet emplacement, le stock passera de <strong>{selectedStock.stock}</strong> à <strong>{selectedStock.stock - 1}</strong>. Le total sera de <strong>{wine.stock - 1}</strong>.</> : <>Le stock total passera de <strong>{wine.stock}</strong> à <strong>{wine.stock - 1}</strong>.</>}</p></div>
        <label className="field"><span>Date de consommation</span><input type="date" required max={today()} value={date} onChange={(event) => setDate(event.target.value)} /></label>
        <label className="field"><span>Note <em>facultative</em></span><textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Un souvenir de dégustation…" /></label>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="modal-actions"><button className="button button-ghost" type="button" onClick={onClose}>Annuler</button><button className="button button-primary" type="submit" disabled={saving || !selectedStock}>{saving ? "Confirmation…" : "Confirmer"}</button></div>
      </form>
    </Modal>
  );
}

function PurchaseModal({ open, wine, locations, places, onClose, onSaved }: { open: boolean; wine: Wine; locations: CellarLocation[]; places: CellarPlaceRecord[]; onClose: () => void; onSaved: (movement: Movement) => Promise<void> }) {
  const [quantity, setQuantity] = useState("1");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [date, setDate] = useState(today());
  const [price, setPrice] = useState("");
  const [seller, setSeller] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const currentStocks = wineLocationStocks(wine).filter((item) => item.stock > 0);
    setQuantity("1");
    setSelectedLocation(
      currentStocks.length === 1
        ? locationValue(currentStocks[0].emplacement_id)
        : locations.length === 1 && currentStocks.length === 0
          ? locations[0].id
          : "",
    );
    setDate(today());
    setPrice("");
    setSeller("");
    setNote("");
    setError("");
  }, [open, wine.id, wine.stock, locations]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const parsedQuantity = Number(quantity);
    if (!Number.isInteger(parsedQuantity) || parsedQuantity < 1) { setError("Indiquez une quantité entière d’au moins 1."); return; }
    if (!selectedLocation) { setError("Choisissez l’emplacement des bouteilles."); return; }
    const parsedPrice = price ? Number(price.replace(",", ".")) : null;
    if (parsedPrice != null && (!Number.isFinite(parsedPrice) || parsedPrice < 0)) { setError("Le prix doit être un montant positif."); return; }
    setSaving(true); setError("");
    try {
      const movement = await api.createMovement(wine.id, { quantite: parsedQuantity, motif: "Achat", date_effective: date || null, prix_unitaire: parsedPrice, vendeur: seller.trim(), note: note.trim(), emplacement_id: locationId(selectedLocation) });
      if (!movement) throw new Error("L’achat n’a pas été créé.");
      await onSaved(movement);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Impossible d’ajouter cet achat."); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Ajouter des bouteilles" description={`Nouvel achat pour ${wine.nom}`}>
      <form onSubmit={submit}>
        <div className="form-grid two-columns">
          <label className="field"><span>Quantité <b>*</b></span><input type="number" min="1" step="1" inputMode="numeric" required value={quantity} onChange={(event) => setQuantity(event.target.value)} /></label>
          <label className="field"><span>Emplacement <b>*</b></span><LocationSelect locations={locations} places={places} value={selectedLocation} onChange={setSelectedLocation} placeholder="Choisir un emplacement…" emptyValue={NO_LOCATION_VALUE} emptyLabel="Sans emplacement" required /></label>
          <label className="field"><span>Date de l’entrée <em>facultative</em></span><input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label>
          <label className="field"><span>Prix par bouteille <em>facultatif</em></span><div className="input-with-suffix"><input type="text" inputMode="decimal" value={price} onChange={(event) => setPrice(event.target.value)} placeholder="0,00" /><span>€</span></div></label>
          <label className="field"><span>Vendeur / provenance <em>facultatif</em></span><input value={seller} onChange={(event) => setSeller(event.target.value)} placeholder="Caviste, domaine…" /></label>
        </div>
        <label className="field"><span>Note sur cette entrée <em>facultative</em></span><textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} /></label>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="modal-actions"><button className="button button-ghost" type="button" onClick={onClose}>Annuler</button><button className="button button-primary" type="submit" disabled={saving || !selectedLocation}>{saving ? "Ajout…" : `Ajouter ${quantity || "0"} bouteille${quantity === "1" ? "" : "s"}`}</button></div>
      </form>
    </Modal>
  );
}

function CorrectionModal({ open, wine, locations, places, onClose, onSaved }: { open: boolean; wine: Wine; locations: CellarLocation[]; places: CellarPlaceRecord[]; onClose: () => void; onSaved: (movement: Movement | null) => Promise<void> }) {
  const [selectedLocation, setSelectedLocation] = useState("");
  const [actual, setActual] = useState("0");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const stocks = wineLocationStocks(wine);
  const selectedId = selectedLocation ? locationId(selectedLocation) : undefined;
  const coravinMinimum = selectedLocation ? activeCoravins(wine, selectedId).length : 0;
  const recorded = selectedLocation
    ? stocks.find((item) => item.emplacement_id === selectedId)?.stock ?? 0
    : 0;
  useEffect(() => {
    if (!open) return;
    const stocked = stocks.filter((item) => item.stock > 0);
    const initial = stocked.length === 1
      ? locationValue(stocked[0].emplacement_id)
      : locations.length === 1 && stocked.length === 0
        ? locations[0].id
        : "";
    setSelectedLocation(initial);
    setActual(String(initial ? stocks.find((item) => item.emplacement_id === locationId(initial))?.stock ?? 0 : 0));
    setNote("");
    setError("");
  }, [open, wine.id, wine.stock, locations]);
  useEffect(() => {
    if (!selectedLocation) return;
    setActual(String(recorded));
  }, [selectedLocation]);
  const delta = Number.isInteger(Number(actual)) ? Number(actual) - recorded : 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedLocation) { setError("Choisissez l’emplacement que vous avez compté."); return; }
    const parsed = Number(actual);
    if (!Number.isInteger(parsed) || parsed < 0) { setError("Indiquez un nombre entier positif ou nul."); return; }
    if (parsed < coravinMinimum) { setError(`Cet emplacement contient ${coravinMinimum} bouteille${coravinMinimum > 1 ? "s" : ""} coravinée${coravinMinimum > 1 ? "s" : ""}. Terminez les bouteilles concernées ou annulez un marquage erroné avant de réduire le stock.`); return; }
    if (parsed === recorded) { await onSaved(null); return; }
    setSaving(true); setError("");
    try {
      const movement = await api.createMovement(wine.id, { quantite: parsed - recorded, motif: "Correction", stock_reel: parsed, date_effective: today(), note: note.trim(), emplacement_id: selectedId ?? null });
      await onSaved(movement);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Correction impossible."); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Corriger le stock" description="Indiquez ce que vous comptez réellement dans la cave." size="small">
      <form onSubmit={submit}>
        <label className="field"><span>Emplacement compté <b>*</b></span><LocationSelect locations={locations} places={places} value={selectedLocation} onChange={setSelectedLocation} placeholder="Choisir un emplacement…" emptyValue={NO_LOCATION_VALUE} emptyLabel="Sans emplacement" required /></label>
        <div className="stock-comparison"><div><span>Stock enregistré ici</span><strong>{selectedLocation ? recorded : "—"}</strong></div><span aria-hidden="true">→</span><label><span>Stock constaté</span><input type="number" min={coravinMinimum} step="1" inputMode="numeric" required disabled={!selectedLocation} value={actual} onChange={(event) => setActual(event.target.value)} /></label></div>
        {coravinMinimum > 0 ? <p className="field-help">Le stock doit inclure les {coravinMinimum} bouteille{coravinMinimum > 1 ? "s" : ""} coravinée{coravinMinimum > 1 ? "s" : ""} de cet emplacement. Pour les retirer, utilisez « Terminer cette bouteille » ou annulez un marquage erroné.</p> : null}
        {delta !== 0 ? <p className="delta-preview">Un mouvement de <strong>{delta > 0 ? "+" : ""}{delta}</strong> sera ajouté à l’historique.</p> : <p className="delta-preview neutral">Le stock est déjà correct.</p>}
        <label className="field"><span>Motif de la correction <em>facultatif</em></span><textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Erreur d’inventaire, bouteille retrouvée…" /></label>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="modal-actions"><button className="button button-ghost" type="button" onClick={onClose}>Annuler</button><button className="button button-primary" type="submit" disabled={saving || !selectedLocation}>{saving ? "Correction…" : "Valider le stock"}</button></div>
      </form>
    </Modal>
  );
}

function TransferModal({ open, wine, locations, places, onClose, onSaved }: { open: boolean; wine: Wine; locations: CellarLocation[]; places: CellarPlaceRecord[]; onClose: () => void; onSaved: (quantity: number) => Promise<void> }) {
  const stocks = wineLocationStocks(wine).filter((item) => item.stock > 0);
  const [source, setSource] = useState("");
  const [destination, setDestination] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [selectedCoravinIds, setSelectedCoravinIds] = useState<string[]>([]);
  const sourceStock = stocks.find(
    (item) => locationValue(item.emplacement_id) === source,
  );
  const sourceCoravins = sourceStock ? activeCoravins(wine, sourceStock.emplacement_id) : [];
  const minimumCoravins = Math.max(0, Number(quantity) - ((sourceStock?.stock ?? 0) - sourceCoravins.length));
  const earliestTransferDate = sourceCoravins.reduce((latest, bottle) =>
    selectedCoravinIds.includes(bottle.id) && bottle.date_coravin > latest ? bottle.date_coravin : latest,
    "",
  );

  useEffect(() => {
    if (!open) return;
    setSource(stocks.length === 1 ? locationValue(stocks[0].emplacement_id) : "");
    setSelectedCoravinIds([]);
    setDestination("");
    setQuantity("1");
    setDate(today());
    setNote("");
    setError("");
  }, [open, wine.id, wine.stock]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const parsedQuantity = Number(quantity);
    if (!sourceStock) { setError("Choisissez l’emplacement de départ."); return; }
    if (!destination) { setError("Choisissez l’emplacement d’arrivée."); return; }
    const destinationId = locationId(destination);
    if (sourceStock.emplacement_id === destinationId) {
      setError("Les emplacements de départ et d’arrivée doivent être différents.");
      return;
    }
    if (!Number.isInteger(parsedQuantity) || parsedQuantity < 1 || parsedQuantity > sourceStock.stock) {
      setError(`Choisissez une quantité comprise entre 1 et ${sourceStock.stock}.`);
      return;
    }
    if (selectedCoravinIds.length > parsedQuantity || selectedCoravinIds.length < minimumCoravins) {
      setError(`Sélectionnez entre ${minimumCoravins} et ${Math.min(parsedQuantity, sourceCoravins.length)} bouteilles coravinées pour ce déplacement.`);
      return;
    }
    if (earliestTransferDate && date && date < earliestTransferDate) {
      setError("La date du déplacement ne peut pas précéder le coravinage des bouteilles sélectionnées.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.transferWine(wine.id, {
        source_emplacement_id: sourceStock.emplacement_id,
        destination_emplacement_id: destinationId,
        quantite: parsedQuantity,
        date_effective: date || null,
        note: note.trim() || null,
        ...(selectedCoravinIds.length ? { coravin_ids: selectedCoravinIds } : {}),
      });
      await onSaved(parsedQuantity);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Impossible de déplacer les bouteilles.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Déplacer des bouteilles" description="Le stock total ne change pas. Le départ et l’arrivée resteront liés dans l’historique.">
      <form onSubmit={submit}>
        <div className="transfer-fields">
          <label className="field">
            <span>Depuis <b>*</b></span>
            <select required value={source} onChange={(event) => { setSource(event.target.value); setDestination(""); setSelectedCoravinIds([]); setError(""); }}>
              <option value="" disabled>Choisir le départ…</option>
              {stocks.map((item) => <option key={locationValue(item.emplacement_id)} value={locationValue(item.emplacement_id)}>{stockLocationLabel(item)} — {bottleLabel(item.stock)}</option>)}
            </select>
          </label>
          <ArrowRight className="transfer-arrow" size={20} aria-hidden="true" />
          <label className="field">
            <span>Vers <b>*</b></span>
            <LocationSelect locations={locations.filter((item) => item.id !== sourceStock?.emplacement_id)} places={places} value={destination} onChange={setDestination} placeholder="Choisir l’arrivée…" emptyValue={NO_LOCATION_VALUE} emptyLabel="Sans emplacement" required />
          </label>
        </div>
        <div className="form-grid two-columns">
          <label className="field"><span>Quantité <b>*</b></span><input type="number" min="1" max={sourceStock?.stock} step="1" inputMode="numeric" required value={quantity} onChange={(event) => setQuantity(event.target.value)} /></label>
          <label className="field"><span>Date du déplacement <em>facultative</em></span><input type="date" min={earliestTransferDate || undefined} value={date} onChange={(event) => setDate(event.target.value)} /></label>
        </div>
        {sourceStock ? <p className="field-help">{bottleLabel(sourceStock.stock)} disponible{sourceStock.stock === 1 ? "" : "s"} au départ.</p> : null}
        {sourceCoravins.length ? (
          <fieldset className="coravin-transfer-selection">
            <legend>Bouteilles coravinées à déplacer</legend>
            <p className="field-help">Cochez les bouteilles coravinées incluses dans la quantité déplacée.{minimumCoravins > 0 ? ` Sélectionnez-en au moins ${minimumCoravins}.` : ""} Leur suivi et leur date de coravinage seront conservés.</p>
            {sourceCoravins.map((bottle, index) => (
              <label key={bottle.id}>
                <input type="checkbox" checked={selectedCoravinIds.includes(bottle.id)} disabled={!selectedCoravinIds.includes(bottle.id) && selectedCoravinIds.length >= Number(quantity)} onChange={(event) => setSelectedCoravinIds((current) => event.target.checked ? [...current, bottle.id] : current.filter((id) => id !== bottle.id))} />
                <span>Bouteille {index + 1} · coravinée le {formatDate(bottle.date_coravin)}{bottle.note ? <small>{bottle.note}</small> : null}</span>
              </label>
            ))}
            {selectedCoravinIds.length > 0 ? <p className="field-help">Une connexion est nécessaire pour déplacer une bouteille coravinée.</p> : null}
          </fieldset>
        ) : null}
        <label className="field"><span>Note <em>facultative</em></span><textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Réorganisation de la cave, transport…" /></label>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="modal-actions"><button className="button button-ghost" type="button" onClick={onClose}>Annuler</button><button className="button button-primary" type="submit" disabled={saving || !source || !destination}><MoveRight size={17} aria-hidden="true" /> {saving ? "Déplacement…" : "Déplacer"}</button></div>
      </form>
    </Modal>
  );
}
